import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ChatSettings } from '@arena/shared';
import {
  type SavedWorldview,
  DEFAULT_CHAT_SETTINGS,
  deleteWorldview,
  getCurrentContent,
  listWorldviews,
  renameWorldview,
  updateWorldviewSettings,
} from '../lib/storage';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'worldview';
}
import { SettingsModal } from './SettingsModal';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function firstHeading(content: string): string | undefined {
  const m = content.match(/^#\s+(.+)$/m);
  return m?.[1]?.trim();
}

function snippet(content: string): string {
  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    if (line.startsWith('#')) continue;
    return line.length > 140 ? `${line.slice(0, 140)}…` : line;
  }
  return lines[0] ?? '';
}

interface WorldviewListProps {
  /** Bump to force a re-read from storage (e.g. after the craft modal closes). */
  refreshKey?: number;
}

export function WorldviewList({ refreshKey = 0 }: WorldviewListProps) {
  const [items, setItems] = useState<SavedWorldview[]>([]);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [settingsId, setSettingsId] = useState<string | null>(null);

  const refresh = () => setItems(listWorldviews());

  useEffect(() => {
    refresh();
  }, [refreshKey]);

  const handleDelete = (id: string) => {
    deleteWorldview(id);
    setConfirmingId(null);
    refresh();
  };

  const handleSettingsChange = (id: string, next: ChatSettings) => {
    updateWorldviewSettings(id, next);
    refresh();
  };

  const handleRename = (id: string, name: string) => {
    renameWorldview(id, name);
    refresh();
  };

  const handleExport = (id: string) => {
    const wv = items.find((w) => w.id === id);
    if (!wv) return;
    const content = getCurrentContent(wv);
    const displayName = wv.name ?? firstHeading(content) ?? 'worldview';
    const filename = `${slugify(displayName)}.md`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const settingsWorldview = settingsId ? items.find((w) => w.id === settingsId) : null;

  if (items.length === 0) return null;

  return (
    <>
      <section className="worldview-list" aria-label="Your saved worldviews">
        <p className="worldview-list__eyebrow">— Your Library —</p>
        <ul className="worldview-list__items">
          {items.map((item) => {
            const content = getCurrentContent(item);
            const versionCount = item.versions.length;
            const isConfirming = confirmingId === item.id;
            return (
              <li
                key={item.id}
                className={`worldview-list__item${isConfirming ? ' worldview-list__item--confirming' : ''}`}
              >
                {!isConfirming && (
                  <>
                    <div className="worldview-list__meta">
                      <h3 className="worldview-list__title">
                        {item.name ?? firstHeading(content) ?? 'Untitled worldview'}
                      </h3>
                      <p className="worldview-list__date">
                        {formatDate(item.updatedAt)}
                        {versionCount > 1 && (
                          <span className="worldview-list__versions">
                            {' · '}
                            {versionCount} versions
                          </span>
                        )}
                      </p>
                      <p className="worldview-list__snippet">{snippet(content)}</p>
                    </div>
                    <div className="worldview-list__actions">
                      <Link
                        to={`/worldview/chat/${item.id}`}
                        className="worldview-list__chat"
                      >
                        Chat <span aria-hidden="true">→</span>
                      </Link>
                      <button
                        type="button"
                        className="worldview-list__settings"
                        aria-label={`Settings for "${firstHeading(content) ?? 'worldview'}"`}
                        title="Chat settings"
                        onClick={() => setSettingsId(item.id)}
                      >
                        ⚙
                      </button>
                      <button
                        type="button"
                        className="worldview-list__delete"
                        aria-label={`Delete "${firstHeading(content) ?? 'worldview'}"`}
                        onClick={() => setConfirmingId(item.id)}
                      >
                        ×
                      </button>
                    </div>
                  </>
                )}
                {isConfirming && (
                  <div className="worldview-list__confirm" role="alertdialog">
                    <p className="worldview-list__confirm-text">
                      Delete this worldview? This can&rsquo;t be undone.
                    </p>
                    <div className="worldview-list__confirm-actions">
                      <button
                        type="button"
                        className="worldview-list__cancel"
                        onClick={() => setConfirmingId(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="worldview-list__delete-confirm"
                        onClick={() => handleDelete(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {settingsWorldview && (
        <SettingsModal
          open={true}
          onClose={() => setSettingsId(null)}
          settings={settingsWorldview.settings ?? DEFAULT_CHAT_SETTINGS}
          onChange={(next) => handleSettingsChange(settingsWorldview.id, next)}
          name={settingsWorldview.name ?? firstHeading(getCurrentContent(settingsWorldview)) ?? ''}
          onRename={(next) => handleRename(settingsWorldview.id, next)}
          onExport={() => handleExport(settingsWorldview.id)}
        />
      )}
    </>
  );
}
